import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";

/* ── tiny canvas: orbiting gold ring around the cube ── */
function OrbitRing() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const S = 56;
    c.width = S;
    c.height = S;
    let t = 0, id;
    const loop = () => {
      ctx.clearRect(0, 0, S, S);
      const cx = S / 2, cy = S / 2, rx = 24, ry = 7;
      /* draw ellipse orbit */
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, Math.PI / 6 + t * 0.3, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(234,179,8,${0.18 + Math.sin(t) * 0.08})`;
      ctx.lineWidth = 0.8;
      ctx.stroke();
      /* orbiting dot */
      const angle = t * 1.2;
      const dx = Math.cos(angle) * rx;
      const dy = Math.sin(angle) * ry;
      /* rotate by Math.PI/6 */
      const rot = Math.PI / 6;
      const px = cx + dx * Math.cos(rot) - dy * Math.sin(rot);
      const py = cy + dx * Math.sin(rot) + dy * Math.cos(rot);
      ctx.beginPath();
      ctx.arc(px, py, 2, 0, Math.PI * 2);
      ctx.fillStyle = "#eab308";
      ctx.fill();
      t += 0.03;
      id = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <canvas
      ref={ref}
      style={{
        position: "absolute", inset: 0,
        width: "100%", height: "100%",
        pointerEvents: "none", zIndex: 10,
      }}
    />
  );
}

/* ── letter-by-letter text animation ── */
const letters = "WORKERS".split("");

export default function ThreeWorkersLogo() {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 14,
      cursor: "pointer", userSelect: "none",
    }}>

      {/* ══ CUBE ICON ══ */}
      <div style={{ position: "relative", width: 56, height: 56, flexShrink: 0 }}>

        {/* outer glow pulse */}
        <motion.div
          animate={{ scale: [1, 1.22, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute", inset: -6,
            borderRadius: 20,
            background: "radial-gradient(ellipse,rgba(234,179,8,0.35) 0%,transparent 70%)",
            zIndex: 0,
          }}
        />

        {/* orbiting ring canvas */}
        <OrbitRing />

        {/* cube face: bottom shadow */}
        <div style={{
          position: "absolute", bottom: -4, left: 4, right: 4, height: 10,
          background: "rgba(234,179,8,0.18)",
          borderRadius: "50%", filter: "blur(6px)",
          zIndex: 0,
        }} />

        {/* main rotating cube */}
        <motion.div
          animate={{ rotateY: [0, 360] }}
          transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
          style={{
            position: "relative", zIndex: 5,
            width: "100%", height: "100%",
            transformStyle: "preserve-3d",
            perspective: 600,
          }}
        >
          {/* cube body */}
          <div style={{
            position: "absolute", inset: 4,
            borderRadius: 14,
            background: "linear-gradient(135deg,#1e3a5f 0%,#0a1628 60%,#0f2040 100%)",
            border: "1px solid rgba(234,179,8,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08), 0 0 0 1px rgba(234,179,8,0.15)",
          }}>
            {/* inner gold shimmer */}
            <motion.div
              animate={{ x: ["-120%", "120%"] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "linear", repeatDelay: 1.5 }}
              style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(105deg,transparent 30%,rgba(234,179,8,0.18) 50%,transparent 70%)",
                borderRadius: 14,
              }}
            />

            {/* the "3" */}
            <motion.span
              animate={{
                textShadow: [
                  "0 0 8px rgba(234,179,8,0.6), 0 2px 4px rgba(0,0,0,0.8)",
                  "0 0 20px rgba(234,179,8,1), 0 2px 4px rgba(0,0,0,0.8)",
                  "0 0 8px rgba(234,179,8,0.6), 0 2px 4px rgba(0,0,0,0.8)",
                ],
              }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              style={{
                fontSize: 26,
                fontWeight: 900,
                fontFamily: "system-ui, -apple-system, sans-serif",
                color: "#eab308",
                lineHeight: 1,
                position: "relative", zIndex: 2,
                letterSpacing: "-0.05em",
              }}
            >
              3
            </motion.span>

            {/* corner accent dots */}
            {[
              { top: 5, left: 5 }, { top: 5, right: 5 },
              { bottom: 5, left: 5 }, { bottom: 5, right: 5 },
            ].map((pos, i) => (
              <motion.div
                key={i}
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.3 }}
                style={{
                  position: "absolute", ...pos,
                  width: 3, height: 3, borderRadius: "50%",
                  background: "#eab308",
                }}
              />
            ))}
          </div>

          {/* top face (3D illusion) */}
          <div style={{
            position: "absolute", top: 0, left: 4, right: 4, height: 6,
            borderRadius: "14px 14px 0 0",
            background: "linear-gradient(90deg,rgba(234,179,8,0.25),rgba(234,179,8,0.08))",
            transform: "rotateX(45deg)",
            transformOrigin: "bottom",
          }} />

          {/* right face (3D illusion) */}
          <div style={{
            position: "absolute", top: 4, right: 0, bottom: 4, width: 5,
            borderRadius: "0 14px 14px 0",
            background: "linear-gradient(180deg,rgba(234,179,8,0.12),rgba(0,0,0,0.3))",
            transform: "rotateY(-45deg)",
            transformOrigin: "left",
          }} />
        </motion.div>
      </div>

      {/* ══ TEXT ══ */}
      <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>

        {/* "3" prefix in gold */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <motion.span
            animate={{
              textShadow: [
                "0 0 6px rgba(234,179,8,0.4)",
                "0 0 16px rgba(234,179,8,0.9)",
                "0 0 6px rgba(234,179,8,0.4)",
              ],
            }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            style={{
              fontSize: 26,
              fontWeight: 900,
              color: "#eab308",
              fontFamily: "system-ui, -apple-system, sans-serif",
              letterSpacing: "-0.05em",
              lineHeight: 1,
            }}
          >
            3
          </motion.span>

          {/* W-O-R-K-E-R-S letter by letter */}
          <div style={{ display: "flex", alignItems: "baseline" }}>
            {letters.map((l, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.04 * i, duration: 0.4, ease: "easeOut" }}
                whileHover={{ color: "#eab308", y: -2 }}
                style={{
                  fontSize: 22,
                  fontWeight: 900,
                  color: "#f0f6ff",
                  fontFamily: "system-ui, -apple-system, sans-serif",
                  letterSpacing: "-0.04em",
                  lineHeight: 1,
                  transition: "color 0.2s, transform 0.2s",
                  display: "inline-block",
                }}
              >
                {l}
              </motion.span>
            ))}
          </div>
        </div>

        {/* tagline */}
        {/* <motion.div
          initial={{ opacity: 0, width: 0 }}
          animate={{ opacity: 1, width: "100%" }}
          transition={{ delay: 0.6, duration: 0.7 }}
          style={{ overflow: "hidden" }}
        >
        
        </motion.div> */}
      </div>
    </div>
  );
}